import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle, ArrowRight, Crown, Zap, Users, Mail, BarChart3, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BillingPage = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [usage, setUsage] = useState({});
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);

  // Ensure "Owner" (unlimited) always shows correctly for the owner account
  const isOwner = (user?.email || '').toLowerCase() === 'leslie@iabadvisors.com';

  useEffect(() => {
    if (user) {
      fetchBillingData();
    }
  }, [user]);

  const fetchBillingData = async () => {
    try {
      // Fetch subscription (requires session cookie) and plans independently
      const subPromise = axios
        .get(`${API}/billing/subscription`, { withCredentials: true })
        .catch(() => null);
      
      // Use /all endpoint if owner, otherwise use public endpoint
      const plansEndpoint = isOwner ? `${API}/billing/plans/all` : `${API}/billing/plans`;
      const plansPromise = axios.get(plansEndpoint, { withCredentials: true });

      const [subscriptionResponse, plansResponse] = await Promise.all([
        subPromise,
        plansPromise
      ]);

      if (subscriptionResponse?.data) {
        setSubscription(subscriptionResponse.data);
        setUsage(subscriptionResponse.data.usage || {});
      }
      setPlans(plansResponse.data.plans || []);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    setUpgrading(true);
    try {
      const response = await axios.post(
        `${API}/billing/checkout`,
        {
          plan_id: planId,
          success_url: `${window.location.origin}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/billing`
        },
        { withCredentials: true }
      );
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout failed:', error);
      alert('Failed to start checkout process. Please try again.');
    } finally {
      setUpgrading(false);
    }
  };

  const getUsageColor = (current, limit) => {
    const percentage = (current / limit) * 100;
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 70) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getUsageWidth = (current, limit) => {
    return Math.min((current / limit) * 100, 100);
  };

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-64 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const ownerPlan = {
    id: 'owner',
    name: 'Owner',
    price: 0,
    contacts_limit: -1,
    users_limit: -1,
    tasks_limit: -1,
    campaigns_limit: -1,
    features: [
      'Full Access',
      'Unlimited Everything',
      'Owner Privileges',
      'All Features'
    ]
  };
  const currentPlan = isOwner
    ? ownerPlan
    : (subscription?.plan || plans.find(p => p.id === 'free'));

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-bissa-blue">Billing & Usage</h1>
          <p className="text-gray-600 mt-2">Manage your subscription and track usage</p>
        </div>
        <div className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-bissa-gold" />
          <span className="font-semibold text-bissa-blue">{currentPlan?.name || 'Free'} Plan</span>
        </div>
      </div>

      {/* Current Plan & Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Usage Overview */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-bissa-blue">Current Usage</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Contacts Usage */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-bissa-blue" />
                  <span className="font-medium">Contacts</span>
                </div>
                <span className={`text-sm font-medium ${getUsageColor(usage.contacts || 0, currentPlan?.contacts_limit || 1500)}`}>
                  {usage.contacts || 0} / {currentPlan?.contacts_limit === -1 ? '∞' : currentPlan?.contacts_limit || 1500}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-bissa-gold rounded-full h-2 transition-all"
                  style={{ width: `${getUsageWidth(usage.contacts || 0, currentPlan?.contacts_limit || 1500)}%` }}
                ></div>
              </div>
            </div>

            {/* Tasks Usage */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-bissa-blue" />
                  <span className="font-medium">Tasks This Month</span>
                </div>
                <span className={`text-sm font-medium ${getUsageColor(usage.tasks_this_month || 0, currentPlan?.tasks_limit || 150)}`}>
                  {usage.tasks_this_month || 0} / {currentPlan?.tasks_limit === -1 ? '∞' : currentPlan?.tasks_limit || 150}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-bissa-gold rounded-full h-2 transition-all"
                  style={{ width: `${getUsageWidth(usage.tasks_this_month || 0, currentPlan?.tasks_limit || 150)}%` }}
                ></div>
              </div>
            </div>

            {/* Campaigns Usage */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-bissa-blue" />
                  <span className="font-medium">Campaigns</span>
                </div>
                <span className={`text-sm font-medium ${getUsageColor(usage.campaigns || 0, currentPlan?.campaigns_limit || 10)}`}>
                  {usage.campaigns || 0} / {currentPlan?.campaigns_limit === -1 ? '∞' : currentPlan?.campaigns_limit || 10}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-bissa-gold rounded-full h-2 transition-all"
                  style={{ width: `${getUsageWidth(usage.campaigns || 0, currentPlan?.campaigns_limit || 10)}%` }}
                ></div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-bissa-blue">Subscription Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="text-2xl font-bold text-bissa-blue">{currentPlan?.name}</div>
              <div className="text-3xl font-bold text-bissa-gold">
                ${currentPlan?.price || 0}
                <span className="text-sm text-gray-600 font-normal">/month</span>
              </div>
            </div>
            
            {subscription?.subscription && (
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Status:</span>
                  <Badge variant={subscription.subscription.status === 'active' ? 'default' : 'secondary'}>
                    {subscription.subscription.status}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span>Next billing:</span>
                  <span>{new Date(subscription.subscription.current_period_end).toLocaleDateString()}</span>
                </div>
              </div>
            )}

            <div className="pt-4 border-t">
              <h4 className="font-semibold text-bissa-blue mb-3">Features</h4>
              <ul className="space-y-2 text-sm">
                {currentPlan?.features?.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-bissa-gold flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Usage Warnings */}
      {!isOwner && ((usage.contacts || 0) / (currentPlan?.contacts_limit || 1500) > 0.8 ||
        (usage.tasks_this_month || 0) / (currentPlan?.tasks_limit || 150) > 0.8 ||
        (usage.campaigns || 0) / (currentPlan?.campaigns_limit || 10) > 0.8) && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-800">Approaching Usage Limits</h4>
                <p className="text-yellow-700 text-sm mt-1">
                  You're approaching your plan limits. Consider upgrading to continue using all features without interruption.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available Plans */}
      <div>
        <h2 className="text-2xl font-bold text-bissa-blue mb-6">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans
            .filter(plan => {
              // Only show Owner plan to leslie@iabadvisors.com
              if (plan.id === 'owner') {
                return isOwner;
              }
              return true; // Show all other plans
            })
            .map((plan) => {
            const isCurrent = plan.id === (subscription?.plan?.id || (isOwner ? 'owner' : 'free'));
            const isUpgrade = !isCurrent && plan.price > (currentPlan?.price || 0);
            
            return (
              <Card key={plan.id} className={`relative ${isCurrent ? 'border-bissa-gold border-2' : 'border-gray-200'}`}>
                {isCurrent && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-bissa-gold text-bissa-blue">Current Plan</Badge>
                  </div>
                )}
                
                <CardHeader className="pb-4">
                  <CardTitle className="text-bissa-blue">{plan.name}</CardTitle>
                  <div className="text-3xl font-bold text-bissa-blue">
                    ${plan.price}
                    <span className="text-sm text-gray-600 font-normal">/month</span>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <ul className="space-y-2 text-sm">
                    <li className="flex justify-between">
                      <span>Contacts:</span>
                      <span className="font-medium">{plan.contacts_limit === -1 ? 'Unlimited' : plan.contacts_limit.toLocaleString()}</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Tasks/month:</span>
                      <span className="font-medium">{plan.tasks_limit === -1 ? 'Unlimited' : plan.tasks_limit.toLocaleString()}</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Campaigns:</span>
                      <span className="font-medium">{plan.campaigns_limit === -1 ? 'Unlimited' : plan.campaigns_limit}</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Users:</span>
                      <span className="font-medium">{plan.users_limit === -1 ? 'Unlimited' : plan.users_limit}</span>
                    </li>
                  </ul>

                  {plan.features && plan.features.length > 0 && (
                    <div className="pt-4 border-t">
                      <ul className="space-y-1 text-xs">
                        {plan.features.slice(0, 3).map((feature, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <CheckCircle className="w-3 h-3 text-bissa-gold flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                        {plan.features.length > 3 && (
                          <li className="text-gray-500">+ {plan.features.length - 3} more features</li>
                        )}
                      </ul>
                    </div>
                  )}

                  <div className="pt-4">
                    {isCurrent ? (
                      <Button disabled className="w-full">Current Plan</Button>
                    ) : plan.id === 'owner' ? (
                      <Button disabled className="w-full">Internal Only</Button>
                    ) : plan.price === 0 ? (
                      <Button disabled className="w-full">Downgrade Not Available</Button>
                    ) : (
                      <Button
                        onClick={() => handleUpgrade(plan.id)}
                        disabled={upgrading}
                        className="w-full bg-bissa-blue text-white hover:bg-bissa-gold hover:text-bissa-blue transition-colors"
                      >
                        {upgrading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <>
                            {isUpgrade ? 'Upgrade' : 'Downgrade'}
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BillingPage;